import type { ChapterPlan, AtomicChapterEvent, AtomicChapterEventSource } from '../types';

export type { AtomicChapterEvent, AtomicChapterEventSource };

const DECISION_MARKERS =
  /\b(decide|decided|choose|chose|commit|resolve|resolves|sacrifice|confront|reveal|discover|refuse|agree)\b/i;
const ENDING_MARKERS =
  /\b(ends?|ending|finally|last|hook|surge|shuts? down|briefly|beneath|underground|water)\b/i;
const ACTION_MARKERS =
  /\b(cross|threaten|sacrifice|reach|resolve|detect|produce|shut|escape|drive|inspect|malfunction|vibrat)\w*\b/i;

export const MAX_ATOMIC_EVENT_CHARS = 200;
const MIN_ATOMIC_EVENT_CHARS = 24;
const MAX_ATOMIC_EVENT_CLAUSES = 3;

export function normalizeEventText(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** Strip duplicated "Chapter must land on:" prefixes. */
export function normalizeEndingTurnText(text: string): string {
  let normalized = normalizeEventText(text);
  while (/^chapter must land on:\s*/i.test(normalized)) {
    normalized = normalized.replace(/^chapter must land on:\s*/i, '').trim();
  }
  return normalized;
}

export function formatChapterEndingState(text: string): string {
  const normalized = normalizeEndingTurnText(text);
  if (!normalized) return 'Chapter ends with changed stakes and a forward hook.';
  return `Chapter must land on: ${normalized}`;
}

function isChapterPurposeCommentary(text: string): boolean {
  const normalized = normalizeEventText(text);
  return /^(this chapter|the chapter (will|should|must)|overall,? this|in this chapter)\b/i.test(normalized);
}

function splitSynopsisIntoSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((part) => normalizeEventText(part))
    .filter((part) => part.length >= MIN_ATOMIC_EVENT_CHARS);
}

function enforceAtomicEventSize(text: string): string[] {
  const normalized = normalizeEventText(text);
  if (!normalized) return [];
  if (normalized.length <= MAX_ATOMIC_EVENT_CHARS) return [normalized];

  const sentences = splitSynopsisIntoSentences(normalized);
  if (sentences.length > 1) {
    return sentences.flatMap((sentence) => enforceAtomicEventSize(sentence));
  }

  const commaParts = normalized.split(/,\s+(?=[A-Z])/).map((part) => normalizeEventText(part));
  if (commaParts.length > 1 && commaParts.every((part) => part.length >= MIN_ATOMIC_EVENT_CHARS)) {
    return commaParts;
  }

  return [normalized.slice(0, MAX_ATOMIC_EVENT_CHARS).trim()];
}

function splitCompoundClauses(sentence: string): string[] {
  const normalized = normalizeEventText(sentence);
  if (!normalized || isChapterPurposeCommentary(normalized)) return [];

  const semicolonParts = normalized
    .split(/;\s*/)
    .map((part) => normalizeEventText(part))
    .filter((part) => part.length >= MIN_ATOMIC_EVENT_CHARS);
  if (semicolonParts.length > 1) {
    return semicolonParts.flatMap((part) => splitCompoundClauses(part));
  }

  const leadingWhileMatch = normalized.match(/^while\s+([^,]+),\s+(.+)$/i);
  if (leadingWhileMatch) {
    const setup = normalizeEventText(`While ${leadingWhileMatch[1]}`);
    const action = normalizeEventText(leadingWhileMatch[2]);
    const parts = [setup, action].filter((part) => part.length >= MIN_ATOMIC_EVENT_CHARS);
    if (parts.length > 1) {
      return parts.flatMap((part) => splitCompoundClauses(part));
    }
  }

  const whichParts = normalized
    .split(/,\s+which\s+/i)
    .map((part, index) => (index === 0 ? normalizeEventText(part) : normalizeEventText(`which ${part}`)))
    .filter((part) => part.length >= MIN_ATOMIC_EVENT_CHARS);
  if (whichParts.length > 1 && whichParts.filter((part) => ACTION_MARKERS.test(part)).length >= 1) {
    return whichParts.flatMap((part) => splitCompoundClauses(part));
  }

  const whileMatch = normalized.match(/^(.+?),\s+while\s+(.+)$/i);
  if (whileMatch) {
    const lead = normalizeEventText(whileMatch[1]);
    const trailing = normalizeEventText(whileMatch[2]);
    const parts = [lead, trailing].filter((part) => part.length >= MIN_ATOMIC_EVENT_CHARS);
    if (parts.length > 1) {
      return parts.flatMap((part) => splitCompoundClauses(part));
    }
  }

  const andParts = normalized
    .split(/,\s+and\s+|\s+and\s+(?=[A-Z][a-z]+\s)/)
    .map((part) => normalizeEventText(part))
    .filter((part) => part.length >= MIN_ATOMIC_EVENT_CHARS);
  if (andParts.length > 1 && andParts.filter((part) => ACTION_MARKERS.test(part)).length >= 2) {
    return andParts.flatMap((part) => splitCompoundClauses(part));
  }

  if (normalized.length <= MAX_ATOMIC_EVENT_CHARS) {
    return [normalized];
  }

  return enforceAtomicEventSize(normalized);
}

function expandDescriptionToAtomicClauses(description: string): string[] {
  const sentences = splitSynopsisIntoSentences(description);
  const sourceSentences = sentences.length > 0 ? sentences : [normalizeEventText(description)];
  const clauses: string[] = [];

  for (const sentence of sourceSentences) {
    const split = splitCompoundClauses(sentence);
    for (const clause of split) {
      if (!clause || isChapterPurposeCommentary(clause)) continue;
      const sized = enforceAtomicEventSize(clause);
      for (const item of sized) {
        if (item.length >= MIN_ATOMIC_EVENT_CHARS) {
          clauses.push(item);
        }
      }
    }
  }

  return clauses.slice(0, 12);
}

function dedupeEvents(events: AtomicChapterEvent[]): AtomicChapterEvent[] {
  const seen = new Set<string>();
  const deduped: AtomicChapterEvent[] = [];
  for (const event of events) {
    const key = normalizeEventText(event.description).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(event);
  }
  return deduped;
}

function scoreDecisionWeight(text: string): number {
  let score = 0;
  if (DECISION_MARKERS.test(text)) score += 2;
  if (ENDING_MARKERS.test(text)) score += 1;
  return score;
}

function beatIdForIndex(index: number): string {
  return `beat-${index + 1}`;
}

/**
 * Assign each atomic event to exactly one primary beat.
 * Ending-turn events belong on Beat 4; decisive events on Beat 3; setup on Beats 1–2.
 */
export function assignPrimaryBeatIds(events: AtomicChapterEvent[]): AtomicChapterEvent[] {
  if (events.length === 0) return events;

  const endingIndex = events.length - 1;
  const decisionCandidates = events
    .map((event, index) => ({ index, weight: scoreDecisionWeight(event.description) }))
    .filter((item) => item.index > 0 && item.index < endingIndex)
    .sort((a, b) => b.weight - a.weight);
  const decisionIndex = decisionCandidates[0]?.index ?? Math.min(2, endingIndex);

  return events.map((event, index) => {
    let primaryBeatId = beatIdForIndex(Math.min(index, 3));
    if (index === endingIndex || event.source === 'ENDING_TURN') {
      primaryBeatId = 'beat-4';
    } else if (index === decisionIndex) {
      primaryBeatId = 'beat-3';
    } else if (index === 0) {
      primaryBeatId = 'beat-1';
    } else if (index === 1) {
      primaryBeatId = 'beat-2';
    } else if (index < decisionIndex) {
      primaryBeatId = index <= 1 ? beatIdForIndex(index) : 'beat-2';
    }
    return { ...event, primaryBeatId };
  });
}

function buildRawEventsFromDescriptions(
  descriptions: string[],
  source: AtomicChapterEventSource,
): Omit<AtomicChapterEvent, 'primaryBeatId'>[] {
  const rawEvents: Omit<AtomicChapterEvent, 'primaryBeatId'>[] = [];
  let eventIndex = 0;

  descriptions.forEach((description, descriptionIndex) => {
    const clauses = expandDescriptionToAtomicClauses(description);
    const isEndingDescription = descriptionIndex === descriptions.length - 1;
    const limitedClauses = clauses.slice(0, MAX_ATOMIC_EVENT_CLAUSES);

    for (const clause of limitedClauses) {
      eventIndex += 1;
      rawEvents.push({
        eventId: `event-${eventIndex}`,
        sequenceHint: eventIndex,
        description: clause,
        requiredOutcome: clause,
        source: isEndingDescription && clause === limitedClauses[limitedClauses.length - 1]
          ? 'ENDING_TURN'
          : source,
      });
    }
  });

  return rawEvents;
}

export function extractAtomicChapterEvents(plan?: ChapterPlan): AtomicChapterEvent[] {
  const roadmap = [...(plan?.requiredBeats || [])].map((beat) => normalizeEventText(beat)).filter(Boolean);
  let rawEvents: Omit<AtomicChapterEvent, 'primaryBeatId'>[] = [];

  if (roadmap.length > 0) {
    rawEvents = buildRawEventsFromDescriptions(roadmap, 'ROADMAP');
  } else if (plan?.summary?.trim()) {
    const sentences = splitSynopsisIntoSentences(plan.summary);
    const descriptions = sentences.length >= 2 ? sentences : [normalizeEventText(plan.summary)];
    rawEvents = buildRawEventsFromDescriptions(descriptions, 'CHAPTER_PURPOSE');
  }

  if (rawEvents.length === 0) {
    return [];
  }

  const deduped = dedupeEvents(
    assignPrimaryBeatIds(
      rawEvents.map((event) => ({
        ...event,
        primaryBeatId: 'beat-1',
      })),
    ),
  );

  return deduped;
}

export function groupAtomicEventsByBeat(
  events: AtomicChapterEvent[],
): Map<string, AtomicChapterEvent[]> {
  const grouped = new Map<string, AtomicChapterEvent[]>([
    ['beat-1', []],
    ['beat-2', []],
    ['beat-3', []],
    ['beat-4', []],
  ]);

  for (const event of events) {
    const bucket = grouped.get(event.primaryBeatId) || [];
    bucket.push(event);
    grouped.set(event.primaryBeatId, bucket);
  }

  return grouped;
}

export function buildAtomicEventDistributionDiagnostic(events: AtomicChapterEvent[]) {
  return events.map((event) => ({
    eventId: event.eventId,
    primaryBeatId: event.primaryBeatId,
    source: event.source,
    descriptionLength: event.description.length,
  }));
}

export function buildInspectorAtomicEventContext(events: AtomicChapterEvent[]): string {
  if (events.length === 0) return 'ATOMIC EVENTS: none';
  return [
    'ATOMIC EVENTS (validate by eventId — do not require verbatim roadmap paragraphs):',
    ...events.map(
      (event) =>
        `- ${event.eventId} [${event.source}] primary=${event.primaryBeatId}: ${event.description}`,
    ),
  ].join('\n');
}

/** Supporting evidence only — Inspector remains authoritative for semantic completion. */
export function collectReportedAtomicEventIds(
  beats: Array<{ requiredEventsSatisfied?: string[] }>,
  events: AtomicChapterEvent[],
): string[] {
  const validIds = new Set(events.map((event) => event.eventId));
  const reported = new Set<string>();
  for (const beat of beats) {
    for (const value of beat.requiredEventsSatisfied || []) {
      if (validIds.has(value)) reported.add(value);
    }
  }
  return [...reported];
}

export function findMissingAtomicEventIds(
  reportedIds: string[],
  events: AtomicChapterEvent[],
): string[] {
  const reported = new Set(reportedIds);
  return events.map((event) => event.eventId).filter((eventId) => !reported.has(eventId));
}
